import { type ComponentPropsWithoutRef, type JSX, type ReactNode } from 'react';

import { Box, EloCardPrimary, EloHeaderCard, EloIcon, Flex } from '@plurall/elo-v3';

import type { EloColorPalette } from '@/lib/elo';

import { SectionCardEmptyState } from './SectionCardEmptyState';

import type { EloIconType, TextProps } from '@plurall/elo-v3';

type Props = {
  title: string | ReactNode;
  description?: string;
  icon?: EloIconType;
  iconColor?: EloColorPalette;
  headerActions?: ReactNode;
  titleProps?: TextProps;
  descriptionProps?: TextProps;
  footer?: ReactNode;
  children?: ReactNode;
  height?: string;
  subgrid?: boolean;
  bodyProps?: ComponentPropsWithoutRef<typeof EloCardPrimary.Body>;
};

const SUBGRID_STYLES = {
  display: 'grid',
  gridTemplateRows: 'subgrid',
  gridRow: 'span 3',
  rowGap: 'gap.large',
} as const;

export function SectionCard({
  title,
  description,
  icon,
  iconColor,
  headerActions,
  titleProps,
  descriptionProps,
  footer,
  children,
  height,
  subgrid = false,
  bodyProps,
}: Props): JSX.Element {
  const card = (
    <EloCardPrimary.Root
      variant='solid'
      behavior='static'
      status='neutral'
      cardRadius='xlarge'
      height={height}
      {...(subgrid && SUBGRID_STYLES)}>
      <EloHeaderCard.Root
        title={title}
        description={description}
        size='large'
        slotsAlignment={icon ? 'stack' : 'inline'}
        titleProps={titleProps}
        descriptionProps={descriptionProps}>
        {icon && (
          <EloHeaderCard.TitleSlot>
            <Flex
              align='center'
              justify='center'
              width='size.40'
              height='size.40'
              borderRadius='border.radius.medium'
              colorPalette={iconColor}
              bg='colorPalette.light.200'>
              <EloIcon
                icon={icon}
                size='medium'
                color='colorPalette.light.1000'
              />
            </Flex>
          </EloHeaderCard.TitleSlot>
        )}

        {headerActions && <EloHeaderCard.SuffixSlot>{headerActions}</EloHeaderCard.SuffixSlot>}
      </EloHeaderCard.Root>

      {children && (
        <EloCardPrimary.Body
          {...bodyProps}
          paddingBlockStart='0'>
          {children}
        </EloCardPrimary.Body>
      )}

      {footer && <EloCardPrimary.Footer>{footer}</EloCardPrimary.Footer>}
    </EloCardPrimary.Root>
  );

  if (!subgrid) {
    return card;
  }

  return (
    <Box
      {...SUBGRID_STYLES}
      css={{ '& > *': SUBGRID_STYLES }}>
      {card}
    </Box>
  );
}

SectionCard.EmptyState = SectionCardEmptyState;
